import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Building2, Download, ExternalLink, Globe, IndianRupee, Receipt, RefreshCw, Search, Sigma, Trash2, Wallet, X } from "lucide-react";
import { EmptyState, KpiCard, PageHeader } from "../../components/common";
import { CollectionsByProperty } from "../../components/CollectionsByProperty";
import { AdvanceCreditReport } from "../../components/AdvanceCreditReport";
import { DrilldownBanner, useUrlFilters } from "../../components/DrilldownBanner";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, staffInTerritory, useFetchGate } from "../../components/FetchBar";
import { StaffName, staffLabelText } from "../../components/StaffAvatar";
import { PaginationBar, TableRowsSkeleton, usePagedList } from "../../components/PaginationBar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import { splitIds, joinIds, namesOf } from "../../components/TerritoryFilter";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, downloadBlob, errMsg, fmtDate, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";
import { LiveBadge } from "../../components/LiveBadge";
import { useRoleModules } from "../../lib/roleModules";
import { ReadOnlyNotice } from "../../components/FeatureGate";
import { DeleteReceiptDialog } from "../../components/DeleteReceiptDialog";   // Phase 177
import { DeletedReceiptsTab } from "../../components/DeletedReceiptsTab";     // Phase 177

const FILTER_KEYS = ["search", "mode", "zone_id", "ward_id", "collector_id", "date_from", "date_to", "source"];

const MODE_STYLES = {
  cash: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20",
  upi: "bg-primary/10 text-primary border-primary/20",
  cheque: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
  "demand draft": "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20",
};
const modeClass = (m) => MODE_STYLES[String(m || "").toLowerCase()] || "bg-muted text-foreground border-border";

const fmtRange = (from, to) => {
  if (from && to) return from === to ? fmtDate(from) : `${fmtDate(from)} – ${fmtDate(to)}`;
  if (from) return `from ${fmtDate(from)}`;
  if (to) return `until ${fmtDate(to)}`;
  return "";
};

/** Phase 133: "Name (EMP-001)" for a staff dropdown entry, flagging inactive staff. */
export const staffOptionLabel = (u) => `${staffLabelText(u.name, u.employee_id)}${u.is_active === false ? " · inactive" : ""}`;

/**
 * Phase 84 — Collections register (receipt-wise). Landing page for the dashboard's
 * payment-mode and collection-trend drilldowns; every filter lives in the URL.
 * Phase 133 — nothing loads until the user clicks Fetch (a drilldown arrival counts as that click);
 * the "Collected by" dropdown lists supervisors + tax collectors with their employee code and follows Zone / Ward.
 */
export default function Collections() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const readOnly = !access.canWrite("reports", "collections");   // Phase 152: View only hides the Excel exports
  // Phase 177: "Delete receipts" feature (OFF by default; granted by the Super Admin) — write = delete / restore, view = tab only
  const canDeleteReceipts = access.canWrite("reports", "delete_receipts");
  const seeDeleted = access.featureAllowed("reports", "delete_receipts");
  const [deleting, setDeleting] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const TABS = ["receipts", "by-property", "advance-credit", "deleted"];
  const tab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "receipts";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t !== "receipts") next.set("tab", t); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  const { filters, patch, clear, arrivedFiltered } = useUrlFilters(FILTER_KEYS);
  const gate = useFetchGate(filters, { autoFetch: arrivedFiltered && tab === "receipts" });
  const [opts, setOpts] = useState({ modes: [], collectors: [], supervisors: [], zones: [], wards: [], sources: [] });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get(`/t/${code}/collections/filters`).then((r) => setOpts({ collectors: [], supervisors: [], ...r.data })).catch(() => {});
  }, [code]);

  // the query always runs on the LAST APPLIED filters (page / size changes reuse them)
  const params = useMemo(() => {
    const p = {};
    const src = gate.applied || {};
    FILTER_KEYS.forEach((k) => { if (src[k]) p[k] = src[k]; });
    return p;
  }, [gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const live = useLiveCollections(code);   // Phase 149: new / reversed payments anywhere refresh the register
  const list = usePagedList(`/t/${code}/collections`, { params, initialLimit: 25,
    enabled: tab === "receipts" && gate.fetched, reloadKey: `${gate.version}:${live}` });
  const summary = list.data?.summary;
  const applied = gate.applied || {};
  const appliedActive = FILTER_KEYS.filter((k) => applied[k]);

  // Phase 176: Zone / Ward are multi-select (comma-joined ids); wards follow the chosen zones
  const zoneSel = splitIds(filters.zone_id), wardSel = splitIds(filters.ward_id);
  const wardsForZone = zoneSel.length ? opts.wards.filter((w) => zoneSel.includes(w.zone_id)) : opts.wards;
  // Phase 133: staff who collect — supervisors + tax collectors — narrowed to the chosen Zone / Ward
  const allStaff = useMemo(() => [...(opts.supervisors || []), ...(opts.collectors || [])], [opts.supervisors, opts.collectors]);
  const staffGroups = useMemo(() => [
    { role: "supervisor", label: "Supervisors", items: (opts.supervisors || []).filter((u) => staffInTerritory(u, filters.zone_id, filters.ward_id)) },
    { role: "tax_collector", label: "Tax Collectors", items: (opts.collectors || []).filter((u) => staffInTerritory(u, filters.zone_id, filters.ward_id)) },
  ].filter((g) => g.items.length), [opts.supervisors, opts.collectors, filters.zone_id, filters.ward_id]);
  const staffCount = staffGroups.reduce((n, g) => n + g.items.length, 0);
  const staffListed = (id, zoneId, wardId) => !id || allStaff.some((u) => u.id === id && staffInTerritory(u, zoneId, wardId));
  const territoryName = wardSel.length
    ? (namesOf(filters.ward_id, opts.wards, "ward_name", "this ward"))
    : zoneSel.length ? (namesOf(filters.zone_id, opts.zones, "zone_name", "this zone")) : "";
  const changeZone = (ids) => {
    const zone_id = joinIds(ids);
    // keep only the wards that still belong to the chosen zones
    const keep = zone_id ? wardSel.filter((w) => opts.wards.some((x) => x.id === w && ids.includes(x.zone_id))) : wardSel;
    const ward_id = joinIds(keep);
    patch({ zone_id, ward_id, collector_id: staffListed(filters.collector_id, zone_id, ward_id) ? filters.collector_id : "" });
  };
  const changeWard = (ids) => {
    const ward_id = joinIds(ids);
    patch({ ward_id, collector_id: staffListed(filters.collector_id, filters.zone_id, ward_id) ? filters.collector_id : "" });
  };
  // staff mapping arrives after the first render — drop a URL-supplied staff id that is not mapped to the chosen area
  useEffect(() => {
    if (allStaff.length && filters.collector_id && !staffListed(filters.collector_id, filters.zone_id, filters.ward_id)) patch({ collector_id: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStaff]);

  const zoneName = namesOf(applied.zone_id, opts.zones, "zone_name");
  const wardName = namesOf(applied.ward_id, opts.wards, "ward_name");
  const staffEntry = allStaff.find((c) => c.id === applied.collector_id);
  const sourceLabel = (opts.sources || []).find((x) => x.value === applied.source)?.label;
  const modes = filters.mode && !opts.modes.includes(filters.mode) ? [...opts.modes, filters.mode] : opts.modes;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(params).toString();
      await downloadBlob(`/t/${code}/collections/export${qs ? `?${qs}` : ""}`,
        `${code}_collections_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Collections exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const clearAll = () => { clear(); gate.reset(); };

  // chips describe the RESULTS on screen, i.e. the applied filters
  const chips = gate.fetched ? [
    { key: "mode", label: "Mode", value: applied.mode },
    { key: "date", label: "Date", value: fmtRange(applied.date_from, applied.date_to) },
    { key: "zone_id", label: "Zone", value: applied.zone_id ? (zoneName || "…") : "" },
    { key: "ward_id", label: "Ward", value: applied.ward_id ? (wardName || "…") : "" },
    { key: "collector_id", label: "Collected by", value: applied.collector_id ? (staffEntry ? staffLabelText(staffEntry.name, staffEntry.employee_id) : "…") : "" },
    { key: "source", label: "Source", value: applied.source ? (sourceLabel || "…") : "" },
    { key: "search", label: "Search", value: applied.search },
  ] : [];

  return (
    <div data-testid="collections-page">
      <PageHeader title="Collections" subtitle={tab === "receipts" ? "Receipt-wise register of every payment collected"
        : tab === "advance-credit" ? "Advance payments: properties that paid more than their demand and still hold advance credit"
        : tab === "deleted" ? "Receipts removed from the register — restorable for 30 days, then only the Super Admin can delete them permanently"
        : "Property-wise totals: demand raised, collected so far, balance due and who is accountable"}>
        <LiveBadge />
        {tab === "receipts" && (
          <>
            <Button variant="outline" size="sm" onClick={() => list.reload()} disabled={!gate.fetched || list.loading} data-testid="collections-refresh-button">
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            {!readOnly && <Button size="sm" onClick={exportExcel} disabled={exporting || !gate.fetched || list.total === 0} data-testid="collections-export-button">
              <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Exporting…" : "Export Excel"}
            </Button>}
          </>
        )}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="collections-tabs">
          <TabsTrigger value="receipts" className="gap-1.5" data-testid="collections-tab-receipts"><Receipt className="h-3.5 w-3.5" /> Receipts</TabsTrigger>
          <TabsTrigger value="by-property" className="gap-1.5" data-testid="collections-tab-by-property"><Building2 className="h-3.5 w-3.5" /> By Property</TabsTrigger>
          <TabsTrigger value="advance-credit" className="gap-1.5" data-testid="collections-tab-advance-credit"><Wallet className="h-3.5 w-3.5" /> Advance Credit</TabsTrigger>
          {seeDeleted && <TabsTrigger value="deleted" className="gap-1.5" data-testid="collections-tab-deleted"><Trash2 className="h-3.5 w-3.5" /> Deleted receipts</TabsTrigger>}
        </TabsList>
      </Tabs>

      {readOnly && <ReadOnlyNotice what="collections" testId="collections-readonly-notice" className="mb-4" />}
      {tab === "deleted" && seeDeleted ? <DeletedReceiptsTab path={`/t/${code}/collections/deleted`} codeOf={() => code} reloadKey={live} testId="deleted-receipts" />
        : tab === "advance-credit" ? <AdvanceCreditReport code={code} readOnly={readOnly} /> : tab === "by-property" ? <CollectionsByProperty code={code} opts={opts} readOnly={readOnly} /> : (
      <div data-testid="collections-receipts-tab">
      <DrilldownBanner chips={chips} onClear={clearAll} testId="collections-drilldown" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4" data-testid="collections-summary">
        <KpiCard label="Receipts" value={gate.fetched ? (summary?.count ?? 0) : "—"} icon={Receipt} testId="collections-kpi-count"
          sub={!gate.fetched ? "click Fetch to load" : appliedActive.length ? "matching current filters" : "all time"} />
        <KpiCard label="Amount collected" value={gate.fetched ? (summary?.amount ?? 0) : "—"} money={gate.fetched} icon={IndianRupee} testId="collections-kpi-amount" />
        <KpiCard label="Average receipt" money={gate.fetched} icon={Sigma} testId="collections-kpi-avg"
          value={gate.fetched ? (summary?.count ? summary.amount / summary.count : 0) : "—"} />
      </div>

      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="collections-filter-form">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search receipt no, PIN, owner, UTR…" value={filters.search}
              onChange={(e) => patch({ search: e.target.value })} data-testid="collections-search-input" />
          </div>
          <Select value={filters.mode || "all"} onValueChange={(v) => patch({ mode: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40" data-testid="collections-mode-select"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              {modes.map((m) => <SelectItem key={m} value={m} data-testid={`collections-mode-option-${m}`}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="w-40"><MultiSelect options={opts.zones.map((z) => ({ value: z.id, label: z.zone_name }))} value={zoneSel} onChange={changeZone}
            allLabel="All zones" noun="zone" placeholder="Search zones…" testId="collections-zone-select" /></div>
          <div className="w-44"><MultiSelect options={wardsForZone.map((w) => ({ value: w.id, label: w.ward_name, group: zoneSel.length !== 1 ? opts.zones.find((z) => z.id === w.zone_id)?.zone_name : undefined }))}
            value={wardSel} onChange={changeWard} allLabel="All wards" noun="ward" placeholder="Search wards…" testId="collections-ward-select" /></div>
          {/* Phase 133: supervisors + tax collectors, "Name (CODE)", narrowed to the chosen Zone / Ward */}
          <Select value={filters.collector_id || "all"} onValueChange={(v) => patch({ collector_id: v === "all" ? "" : v })}>
            <SelectTrigger className="w-56" data-testid="collections-collector-select" data-staff-count={staffCount}>
              <SelectValue placeholder="Collected by" />
            </SelectTrigger>
            <SelectContent data-testid="collections-collector-options">
              <SelectItem value="all" data-testid="collections-collector-option-all">
                All staff{territoryName ? ` in ${territoryName}` : ""}
              </SelectItem>
              {staffGroups.map((g) => (
                <SelectGroup key={g.role} data-testid={`collections-staff-group-${g.role}`}>
                  <SelectLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">{g.label}</SelectLabel>
                  {g.items.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`collections-collector-option-${u.id}`} data-role={u.role}>
                      {staffOptionLabel(u)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
              {staffCount === 0 && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground" data-testid="collections-collector-empty">
                  {territoryName ? `No staff mapped to ${territoryName}` : "No staff found"}
                </div>
              )}
            </SelectContent>
          </Select>
          {/* Phase 118: where the receipt came from — Citizen Portal payments stay distinguishable */}
          <Select value={filters.source || "all"} onValueChange={(v) => patch({ source: v === "all" ? "" : v })}>
            <SelectTrigger className="w-44" data-testid="collections-source-select"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {(opts.sources || []).map((x) => <SelectItem key={x.value} value={x.value} data-testid={`collections-source-option-${x.value}`}>{x.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Input type="date" className="h-9 w-[150px]" value={filters.date_from} max={filters.date_to || undefined}
              onChange={(e) => patch({ date_from: e.target.value })} aria-label="From date" data-testid="collections-date-from" />
            <span className="text-xs text-muted-foreground">to</span>
            <Input type="date" className="h-9 w-[150px]" value={filters.date_to} min={filters.date_from || undefined}
              onChange={(e) => patch({ date_to: e.target.value })} aria-label="To date" data-testid="collections-date-to" />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <FetchDirtyHint dirty={gate.dirty} testId="collections-fetch-dirty" />
            <FetchButton type="submit" size="sm" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched}
              testId="collections-fetch-button" />
            {FILTER_KEYS.some((k) => filters[k]) && (
              <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clearAll} data-testid="collections-clear-filters">
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
        </FetchForm>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId="collections-fetch-prompt"
              subtitle="Choose a date range, zone, ward, staff member or payment mode above, then click Fetch to load the receipts." />
          ) : list.error && !list.data ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center" data-testid="collections-error">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm text-muted-foreground">Could not load collections.</p>
              <Button size="sm" variant="outline" onClick={() => list.reload()} data-testid="collections-error-retry">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          ) : !list.data ? (
            <TableRowsSkeleton rows={8} cols={6} testId="collections-skeleton" />
          ) : list.items.length === 0 ? (
            <EmptyState title="No receipts match" subtitle={appliedActive.length ? "Try widening the date range or clearing a filter, then Fetch again." : "Payments will appear here as collectors record them."}
              action={appliedActive.length ? <Button variant="outline" size="sm" onClick={clearAll} data-testid="collections-empty-clear">Clear filters</Button> : null} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table data-testid="collections-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Receipt</TableHead>
                      <TableHead>Property</TableHead>
                      <TableHead className="hidden md:table-cell">Ward</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead className="hidden lg:table-cell">Collected by</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      {canDeleteReceipts && <TableHead className="w-12 text-right"><span className="sr-only">Actions</span></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.items.map((p) => (
                      <TableRow key={p.id} className="hover:bg-muted/60" data-testid={`collection-row-${p.receipt_number || p.id}`}>
                        <TableCell className="whitespace-nowrap">
                          <p className="text-sm tabular-nums">{fmtDate(p.payment_date)}</p>
                          {p.payment_time && <p className="text-xs text-muted-foreground tabular-nums">{String(p.payment_time).slice(0, 5)}</p>}
                        </TableCell>
                        <TableCell className="font-mono text-sm whitespace-nowrap">
                          {p.receipt_number ? (
                            <Link to={`/${code}/receipt/${p.receipt_number}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`collection-receipt-link-${p.receipt_number}`}>
                              {p.receipt_number} <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            </Link>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {p.pin ? (
                            <Link to={`/${code}/admin/properties?focus=${p.property_id}`} className="font-mono text-sm text-primary hover:underline"
                              data-testid={`collection-pin-link-${p.pin}`}>{p.pin}</Link>
                          ) : <span className="font-mono text-sm text-muted-foreground">—</span>}
                          <p className="text-xs text-muted-foreground truncate max-w-[180px]">{p.owner_name || ""}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          <p>{p.ward_label || "—"}</p>
                          {p.zone_label && <p className="text-xs text-muted-foreground">{p.zone_label}</p>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`font-medium ${modeClass(p.payment_mode)}`} data-testid={`collection-mode-badge-${p.id}`}>
                            {p.payment_mode || "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {p.source === "citizen_portal" ? (
                            <div className="space-y-0.5" data-testid={`collection-portal-cell-${p.id}`}>
                              <Badge variant="outline" className="text-[10px] font-normal gap-1 bg-accent text-accent-foreground border-primary/20" data-testid={`collection-portal-badge-${p.id}`}>
                                <Globe className="h-3 w-3" /> Citizen Portal
                              </Badge>
                              {p.collected_by
                                ? <p className="text-xs text-muted-foreground">credited to <StaffName code={code} userId={p.collected_by} name={p.collected_by_name || "Staff"} empCode={p.collected_by_employee_id} size="xs" nameClassName="text-xs" /></p>
                                : <p className="text-xs text-muted-foreground">unassigned property · paid by {p.payer_name || "citizen"}</p>}
                            </div>
                          ) : p.collected_by ? (
                            <div className="space-y-0.5">
                              <StaffName code={code} userId={p.collected_by} name={p.collected_by_name || "Staff"} empCode={p.collected_by_employee_id} size="xs" nameClassName="text-sm" />
                              {(p.source === "legacy_import" || p.collection_channel === "import") && (
                                <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200" data-testid={`collection-legacy-badge-${p.id}`}>Imported · credited</Badge>
                              )}
                            </div>
                          ) : p.source === "legacy_import" || p.collection_channel === "import"
                              ? <Badge variant="outline" className="text-[10px] font-normal bg-violet-50 text-violet-800 border-violet-200" data-testid={`collection-legacy-badge-${p.id}`}>Imported (Legacy)</Badge>
                              : <span className="text-muted-foreground">{p.collected_by_name || "Online / Citizen"}</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">{fmtINR(p.amount)}</TableCell>
                        {canDeleteReceipts && (
                          <TableCell className="text-right">
                            {p.status === "success" && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" title="Delete this receipt"
                                onClick={() => setDeleting(p)} data-testid={`collection-delete-${p.receipt_number || p.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="px-4 pb-3 border-t">
                <PaginationBar page={list.page} limit={list.limit} total={list.total} loading={list.loading}
                  onPageChange={list.setPage} onLimitChange={list.setLimit} testId="collections-pagination" />
              </div>
            </>
          )}
        </CardContent>
      </Card>
      </div>
      )}
      {/* Phase 177: delete one receipt (mandatory reason) */}
      <DeleteReceiptDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)} receipt={deleting} code={code}
        onDeleted={() => list.reload()} testId="delete-receipt" />
    </div>
  );
}
